import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  Progress,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconChevronRight, IconFileText, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { PickStatusBadge } from './PickStatusBadge';
import { listPicks, type PickDto } from './api';

type Scope = 'open' | 'toShip' | 'all';

/**
 * Picks — the work on the floor.
 *
 * **Oldest first**, unlike every other list in this application, and the server sorts it that way
 * too. A pick list is a queue: the order released first is the one that should be walked first, and
 * putting the newest at the top buries whoever has been waiting longest.
 *
 * **Opens on open**, which is legacy's `IsShipped = 0` on sp_WebOrderPickHdr_GetAll. The question
 * somebody brings to this screen is "what is left to pick", not "what have we ever picked".
 *
 * **There is no New button.** Nothing creates a pick here: releasing a sales order does, and that
 * is deliberate — a second way to make one exist is a second way for stock to be promised twice.
 */
export function PicksRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scope, setScope] = useState<Scope>('open');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['picks', scope] as const,
    queryFn: ({ signal }) =>
      listPicks({ openOnly: scope === 'open', toShip: scope === 'toShip' }, signal),
  });

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (p) =>
        p.pickNumber.toLowerCase().includes(needle) ||
        (p.orderNumber?.toLowerCase().includes(needle) ?? false) ||
        (p.customerName?.toLowerCase().includes(needle) ?? false) ||
        (p.customerPo?.toLowerCase().includes(needle) ?? false),
    );
  }, [query.data, search]);

  function openPick(id: string) {
    void navigate({ to: '/app/picks/$id', params: { id } });
  }

  function openPackingList(id: string) {
    void navigate({ to: '/app/picks/$id/packing-list', params: { id } });
  }

  const columns: DataColumn<PickDto>[] = useMemo(
    () => [
      {
        key: 'number',
        label: t('picks.number'),
        card: 'title',
        render: (p) => (
          <Anchor fw={500} onClick={() => openPick(p.pickId)}>
            <Code>{p.pickNumber}</Code>
          </Anchor>
        ),
      },
      {
        key: 'order',
        label: t('picks.order'),
        render: (p) => <Text size="sm">{p.orderNumber ?? '—'}</Text>,
      },
      {
        key: 'customer',
        label: t('picks.customer'),
        card: 'subtitle',
        render: (p) => p.customerName ?? '—',
      },
      {
        key: 'customerPo',
        label: t('picks.customerPo'),
        render: (p) => <Text size="sm">{p.customerPo ?? '—'}</Text>,
      },
      {
        key: 'requested',
        label: t('picks.requested'),
        render: (p) => (
          <Text size="sm">
            {p.requestedShipDateUtc
              ? new Date(p.requestedShipDateUtc).toLocaleDateString()
              : '—'}
          </Text>
        ),
      },
      {
        key: 'progress',
        label: t('picks.progress'),
        // Lines, never quantities. The lines of one pick are each in their own unit, so "40 of 60
        // picked" would be adding cases to eaches and would be wrong by a factor nobody can see.
        render: (p) => {
          const done = p.lineCount - p.linesOutstanding;
          return (
            <Stack gap={4} miw={110}>
              <Text size="sm">{t('picks.linesDone', { done, total: p.lineCount })}</Text>
              <Progress
                value={p.lineCount === 0 ? 0 : (done / p.lineCount) * 100}
                color={p.linesOutstanding === 0 ? 'green' : 'invetrak'}
                size="sm"
                aria-label={t('picks.linesDone', { done, total: p.lineCount })}
              />
            </Stack>
          );
        },
      },
      {
        key: 'released',
        label: t('picks.released'),
        render: (p) => <Text size="sm">{new Date(p.createdAtUtc).toLocaleString()}</Text>,
      },
      {
        key: 'status',
        label: t('picks.status'),
        card: 'aside',
        render: (p) => (
          <Group gap={4} wrap="nowrap">
            <PickStatusBadge status={p.status} />
            {p.isShipped && (
              <Badge color="teal" variant="light">
                {t('picks.shippedBadge')}
              </Badge>
            )}
            {p.isClosedShort && !p.isShipped && (
              <Badge color="orange" variant="light">
                {t('picks.shortBadge')}
              </Badge>
            )}
          </Group>
        ),
      },
    ],
    // openPick closes over navigate only, which is stable for the life of the router.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  );

  return (
    <>
      <PageHeader title={t('nav.picks')} />

      <Stack gap="md">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            label={t('picks.search')}
            placeholder={t('picks.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 300 }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('picks.show')}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { label: t('picks.openOnly'), value: 'open' },
                // Without this tab shipping is unreachable: a pick becomes `completed` the
                // moment it is picked or closed short and drops straight off the work list.
                { label: t('picks.toShip'), value: 'toShip' },
                { label: t('picks.all'), value: 'all' },
              ]}
            />
          </Stack>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('picks.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('picks.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim()
              ? t('picks.noMatch')
              : scope === 'open'
                ? t('picks.noneOpen')
                : scope === 'toShip'
                  ? t('picks.noneToShip')
                  : t('picks.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(p) => p.pickId}
            minWidth={900}
            striped
            highlightOnHover
            actionsWidth={100}
            actions={(p) => (
              <Group gap={2} wrap="nowrap" justify="flex-end">
                {/*
                  Only once it has gone. Before that there is no document -- the list is written at
                  ship confirm -- and offering the link would send people to an empty page.
                */}
                {p.isShipped && (
                  <ActionIcon
                    variant="subtle"
                    color="invetrak"
                    aria-label={t('picks.packing.openFor', { number: p.pickNumber })}
                    onClick={() => openPackingList(p.pickId)}
                  >
                    <IconFileText size={16} />
                  </ActionIcon>
                )}
                <ActionIcon
                  variant="subtle"
                  color="invetrak"
                  aria-label={t('picks.openPick', { number: p.pickNumber })}
                  onClick={() => openPick(p.pickId)}
                >
                  <IconChevronRight size={16} />
                </ActionIcon>
              </Group>
            )}
          />
        )}
      </Stack>
    </>
  );
}
